import { ChangeEvent, FocusEvent, FocusEventHandler, useEffect, useRef, useState } from 'react';
import MaskedInput from 'react-text-mask';

import moment, { Moment } from 'moment';

import { TextMaskProps } from './TextMaskProps.types';
import { useFixCaptureAttr } from './hooks/useFixCaptureAttr';

type DateMaskProps = TextMaskProps & {
  minDate?: Moment;
  maxDate?: Moment;
  shouldSkipBlurValidation?: (e: FocusEvent<HTMLInputElement>) => boolean;
};

const DATE_FORMAT = 'MMDDYYYY';
const PLACEHOLDER_CHAR = '_';

// Mask for MM/DD/YYYY format
const dateMask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];

// Helper function to get maximum days in a month, accounting for leap years
const getMaxDaysInMonth = (month: number, year: number): number => {
  // moment.js uses 0-indexed months, but our input is 1-indexed
  const momentDate = moment()
    .year(year)
    .month(month - 1);
  return momentDate.daysInMonth();
};

// Creates a pipe function for normalizing structure (ensuring _ in deleted positions)
// and validating date with optional min/max constraints
const createDatePipe = (minDate?: Moment, maxDate?: Moment, validateOnly = false) => {
  const minDateStr = minDate?.format(DATE_FORMAT);
  const maxDateStr = maxDate?.format(DATE_FORMAT);

  // Default constraints
  const defaultMinMonth = '01';
  const defaultMinDay = '01';
  const defaultMinYear = '1970';

  const defaultMaxMonth = '12';
  const defaultMaxDay = '31';
  const defaultMaxYear = '2100';

  // Parse min/max dates
  const minMonth = minDateStr?.substring(0, 2) || defaultMinMonth;
  const minDay = minDateStr?.substring(2, 4) || defaultMinDay;
  const minYear = minDateStr?.substring(4, 8) || defaultMinYear;

  const maxMonth = maxDateStr?.substring(0, 2) || defaultMaxMonth;
  const maxDay = maxDateStr?.substring(2, 4) || defaultMaxDay;
  const maxYear = maxDateStr?.substring(4, 8) || defaultMaxYear;

  return (conformedValue: string) => {
    // Normalize structure: ensure all digit positions are either digits or _
    const chars = conformedValue.split('');
    const result = [...chars];

    // Ensure structure is correct: MM/DD/YYYY
    // Positions 0,1 = month; 3,4 = day; 6,7,8,9 = year
    const digitPositions = [0, 1, 3, 4, 6, 7, 8, 9];

    for (const pos of digitPositions) {
      if (pos >= result.length) {
        // Shouldn't happen with proper mask, but handle it
        continue;
      }

      const char = result[pos];

      // If position is empty or not a digit/placeholder, set to placeholder
      if (!char || (!/\d/.test(char) && char !== PLACEHOLDER_CHAR && char !== '/')) {
        result[pos] = PLACEHOLDER_CHAR;
      }
    }

    // If only normalizing structure, return early
    if (validateOnly === false) {
      return result.join('');
    }

    // Continue with validation logic
    // conformedValue is like "02/03/2026" or "__/__/____" or "0_/__/____"
    // Extract parts from formatted string
    const mm = [result[0], result[1]].filter(c => c && c !== PLACEHOLDER_CHAR && /\d/.test(c)).join('');
    const dd = [result[3], result[4]].filter(c => c && c !== PLACEHOLDER_CHAR && /\d/.test(c)).join('');
    const yyyy = [result[6], result[7], result[8], result[9]]
      .filter(c => c && c !== PLACEHOLDER_CHAR && /\d/.test(c))
      .join('');

    // If any field is incomplete, don't validate - return normalized structure
    if (mm.length > 0 && mm.length < 2) {
      return result.join('');
    }

    if (dd.length > 0 && dd.length < 2) {
      return result.join('');
    }

    if (yyyy.length > 0 && yyyy.length < 4) {
      return result.join('');
    }

    // Validate month (01-12) - only if complete
    if (mm.length === 2) {
      const monthStr = result[0] + result[1];
      const monthNum = parseInt(monthStr, 10);

      // Validate month range: 01-12
      if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
        // If invalid, clamp to valid range
        let clampedMonth = monthNum;
        if (isNaN(monthNum) || monthNum < 1) {
          clampedMonth = 1;
        } else if (monthNum > 12) {
          clampedMonth = 12;
        }

        const clampedStr = clampedMonth.toString().padStart(2, '0');
        result[0] = clampedStr[0];
        result[1] = clampedStr[1];
      }
    }

    // Validate day (01-31) - only if complete
    if (dd.length === 2) {
      const monthStr = result[0] + result[1];
      const monthNum = parseInt(monthStr, 10);
      const yearStr = result[6] + result[7] + result[8] + result[9];
      const yearNum = parseInt(yearStr, 10);

      const dayStr = result[3] + result[4];
      const dayNum = parseInt(dayStr, 10);

      // Determine maximum days in the month
      let maxDaysInMonth = 31;
      if (mm.length === 2 && yyyy.length === 4 && !isNaN(monthNum) && !isNaN(yearNum)) {
        maxDaysInMonth = getMaxDaysInMonth(monthNum, yearNum);
      }

      // Validate day range: 01 to maxDaysInMonth
      if (isNaN(dayNum) || dayNum < 1 || dayNum > maxDaysInMonth) {
        // If invalid, clamp to valid range
        let clampedDay = dayNum;
        if (isNaN(dayNum) || dayNum < 1) {
          clampedDay = 1;
        } else if (dayNum > maxDaysInMonth) {
          clampedDay = maxDaysInMonth;
        }

        const clampedStr = clampedDay.toString().padStart(2, '0');
        result[3] = clampedStr[0];
        result[4] = clampedStr[1];
      }
    }

    // Validate year - only if complete
    if (yyyy.length === 4) {
      const yearStr = result[6] + result[7] + result[8] + result[9];
      const yearNum = parseInt(yearStr, 10);

      if (isNaN(yearNum)) {
        return result.join('');
      }

      // Clamp year to min/max range
      let validatedYear = yearNum;
      if (yearNum < parseInt(minYear, 10)) {
        validatedYear = parseInt(minYear, 10);
      } else if (yearNum > parseInt(maxYear, 10)) {
        validatedYear = parseInt(maxYear, 10);
      }

      const validatedYearStr = validatedYear.toString().padStart(4, '0');
      result[6] = validatedYearStr[0];
      result[7] = validatedYearStr[1];
      result[8] = validatedYearStr[2];
      result[9] = validatedYearStr[3];

      // Apply year-specific constraints only if all fields are complete
      if (mm.length === 2 && dd.length === 2) {
        const effectiveMinMonth = validatedYear === parseInt(minYear, 10) ? minMonth : defaultMinMonth;
        const effectiveMaxMonth = validatedYear === parseInt(maxYear, 10) ? maxMonth : defaultMaxMonth;

        const monthStr = result[0] + result[1];
        const monthNum = parseInt(monthStr, 10);

        if (!isNaN(monthNum)) {
          const minMonthNum = parseInt(effectiveMinMonth, 10);
          const maxMonthNum = parseInt(effectiveMaxMonth, 10);

          let clampedMonth = monthNum;
          if (monthNum < minMonthNum) {
            clampedMonth = minMonthNum;
          } else if (monthNum > maxMonthNum) {
            clampedMonth = maxMonthNum;
          }

          const clampedMonthStr = clampedMonth.toString().padStart(2, '0');
          result[0] = clampedMonthStr[0];
          result[1] = clampedMonthStr[1];

          const dayStr = result[3] + result[4];
          const dayNum = parseInt(dayStr, 10);

          if (!isNaN(dayNum)) {
            const effectiveMinDay =
              validatedYear === parseInt(minYear, 10) && clampedMonth === parseInt(minMonth, 10)
                ? minDay
                : defaultMinDay;
            const effectiveMaxDay =
              validatedYear === parseInt(maxYear, 10) && clampedMonth === parseInt(maxMonth, 10)
                ? maxDay
                : getMaxDaysInMonth(clampedMonth, validatedYear).toString().padStart(2, '0');

            const minDayNum = parseInt(effectiveMinDay, 10);
            const maxDayNum = parseInt(effectiveMaxDay, 10);

            let clampedDay = dayNum;
            if (dayNum < minDayNum) {
              clampedDay = minDayNum;
            } else if (dayNum > maxDayNum) {
              clampedDay = maxDayNum;
            }

            const clampedDayStr = clampedDay.toString().padStart(2, '0');
            result[3] = clampedDayStr[0];
            result[4] = clampedDayStr[1];
          }
        }
      }
    }

    return result.join('');
  };
};

export const DateMask = ({
  inputRef,
  capture,
  minDate,
  maxDate,
  shouldSkipBlurValidation,
  ...restProps
}: DateMaskProps) => {
  const fixedCapture = useFixCaptureAttr(capture);

  const inputRefInternal = useRef<HTMLInputElement | null>(null);

  const [date, setDate] = useState(restProps.value?.toString() || '');

  // Pipe for validation on blur
  const validatePipe = createDatePipe(minDate, maxDate, true);

  // Handle input change
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setDate(newValue);

    if (restProps.onChange) {
      // Pass the value as-is (react-text-mask already formats it)
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: newValue, name: restProps.name },
      };

      restProps.onChange(syntheticEvent as ChangeEvent<HTMLInputElement>);
    }
  };

  // Validate on blur using pipe
  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    if (shouldSkipBlurValidation && shouldSkipBlurValidation(e)) {
      if (restProps.onBlur) {
        restProps.onBlur(e);
      }

      return;
    }

    const input = e.target;
    const currentValue = input.value;

    // Apply validation on blur
    const validated = validatePipe(currentValue);
    if (validated !== currentValue) {
      setDate(validated);

      input.value = validated;

      if (restProps.onChange) {
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: validated, name: restProps.name },
        };

        restProps.onChange(syntheticEvent as ChangeEvent<HTMLInputElement>);
      }
    }

    if (restProps.onBlur) {
      restProps.onBlur(e);
    }
  };

  // Sync internal state with external value prop
  useEffect(() => {
    const externalValue = restProps.value?.toString() || '';

    if (externalValue !== date) {
      setDate(externalValue);
    }
  }, [restProps.value]);

  return (
    <MaskedInput
      ref={ref => {
        const inputElement = ref ? (ref.inputElement as HTMLInputElement) : null;
        inputRefInternal.current = inputElement;
        inputRef(inputElement);
      }}
      {...restProps}
      value={date}
      capture={fixedCapture}
      mask={dateMask}
      guide={false}
      disabled={restProps.disabled}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
